import React, { useState, useEffect } from 'react';
import {
    CardContent,
    Typography,
    TextField,
    IconButton,
    Button,
    Grid,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    RadioGroup,
    FormControl,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    StyledCard,
    CardContentBox,
    FieldBox,
    StyledRadio, 
    StyledFormControlLabel,
} from './UserProfile.styles';
import { getProfile, updateProfile, getProfileByOrderId, updateAddress } from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';
import CountrySelect from '../CountriesDropBox/CountriesDropBox';

const InfoCard = ({
    title,
    fields,
    onFieldChange,
    isEditing,
    toggleEdit,
    showEdit = false,
    onAdd,
    onDelete,
    readOnly,
    lockedFieldKeys = [],
}) => (
    <StyledCard variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ padding: 1 }}>
            <CardContentBox>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <Box>
                    {!readOnly && showEdit && (
                        <IconButton size="small" onClick={toggleEdit}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                    {!readOnly && onAdd && (
                        <Button
                            startIcon={<AddIcon fontSize="small" />}
                            size="small"
                            variant="outlined"
                            onClick={onAdd}
                            sx={{ fontSize: '0.7rem', mr: 1 }}
                        >
                            Add
                        </Button>
                    )}
                    {!readOnly && onDelete && (
                        <IconButton size="small" onClick={onDelete}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </CardContentBox>

            {fields.map(({ label, key, value }, idx) => (
                <FieldBox key={key || idx}>
                    {key === 'country' ? (
                        <Box sx={{ width: '100%', mt: 0.5, mb: 0.5 }}>
                            <CountrySelect
                                value={value || ''}
                                onChange={(newValue) => {
                                    if (!readOnly && isEditing) {
                                        onFieldChange(key, newValue);
                                    }
                                }}
                                error={false}
                                helperText=""
                                disabled={readOnly || !isEditing}
                                sx={{ width: '100%' }}
                            />
                        </Box>
                    ) : (
                        <TextField
                            fullWidth
                            size="small"
                            margin="dense"
                            label={label}
                            value={value || ''}
                            onChange={(e) =>
                                !readOnly &&
                                isEditing &&
                                !lockedFieldKeys.includes(key) &&
                                onFieldChange(key, e.target.value)
                            }
                            InputProps={{
                                readOnly:
                                    readOnly ||
                                    !isEditing ||
                                    lockedFieldKeys.includes(key),
                            }}
                            InputLabelProps={{ style: { fontSize: '0.8rem' } }}
                            sx={{
                                '& input': {
                                    fontSize: '0.85rem',
                                    padding: '6px 10px',
                                },
                            }}
                        />
                    )}
                </FieldBox>
            ))}
        </CardContent>
    </StyledCard>
);

const UserProfile = ({ readOnly = false, isAdmin = false, orderId = null }) => {
    const { showToast } = useToast();
    const theme = useTheme();

    const accordionSx = {
        mb: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        bgcolor: 'background.paper',
        '&:before': { display: 'none' },
    };
    const accordionSummarySx = {
        minHeight: 52,
        px: 2,
        bgcolor: alpha(theme.palette.grey[500], 0.06),
        '& .MuiAccordionSummary-content': { my: 1.25 },
    };
    const accordionDetailsSx = { px: 2, py: 2, pt: 1.5 };
    const [cards, setCards] = useState({
        customerInfo: [],
        personalAddress: [],
        businessInfo: [],
        defaultShipping: [],
        otherShipping: [],
    });

    const [editingCards, setEditingCards] = useState({
        customerInfo: false,
        personalAddress: false,
        businessInfo: false,
        defaultShipping: false,
        otherShipping: [],
    });

    const [expanded, setExpanded] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [accountType, setAccountType] = useState('Individual');
    /** Company sub-account: only name + mobile editable; addresses/business from primary */
    const subUserMode = Boolean(profileData?.is_business_subuser);

    const handleAccordionChange = (panel) => (_event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const fieldMap = {
        street: 'street_address',
        suburb: 'suburb_locality',
        city: 'city_town',
        state: 'state_province_region',
        postal: 'postal_zip_code',
        country: 'country',
    };

    useEffect(() => {
        const loadProfile = async () => {
            try {
                let profile;
                if (isAdmin && orderId) {
                    profile = await getProfileByOrderId(orderId);
                } else {
                    profile = await getProfile();
                }

                setProfileData(profile);

                const personalAddress =
                    profile.addresses?.find((addr) => addr.is_billing) || {};

                const defaultShipping =
                    profile.addresses?.find(
                        (addr) => addr.is_shipping && addr.is_default
                    ) || {};

                const otherShippingAddresses =
                    profile.addresses?.filter(
                        (addr) => addr.is_shipping && !addr.is_default
                    ) || [];

                const businessInfo = profile.businessInformation?.[0] || {};

                if (profile.account_type) {
                    setAccountType(profile.account_type);
                }

                const mapAddressToFields = (addr) => {
                    // Always return all fields, even if address is empty object
                    // This ensures the UI always shows all address fields
                    if (!addr || typeof addr !== 'object') {
                        addr = {};
                    }
                    return [
                        { key: 'street', label: 'Street Address', value: addr.street_address ?? '' },
                        { key: 'suburb', label: 'Suburb / Locality', value: addr.suburb_locality ?? '' },
                        { key: 'city', label: 'City / Town', value: addr.city_town ?? '' },
                        { key: 'state', label: 'State / Province / Region', value: addr.state_province_region ?? '' },
                        { key: 'postal', label: 'Postal / ZIP Code', value: addr.postal_zip_code ?? '' },
                        { key: 'country', label: 'Country', value: addr.country ?? '' },
                    ];
                };

                setCards({
                    customerInfo: [
                        { key: 'fullName', label: 'Full Name', value: profile.full_name || '' },
                        { key: 'email', label: 'Email Address', value: profile.email || '' },
                        { key: 'mobile', label: 'Mobile No.', value: profile.mobile_no || '' },
                    ],
                    personalAddress: mapAddressToFields(personalAddress),
                    defaultShipping: mapAddressToFields(defaultShipping),
                    businessInfo: [
                        { key: 'legalName', label: 'Legal Business Name', value: businessInfo.legal_business_name || '' },
                        { key: 'regNumber', label: 'Registration Number (optional)', value: businessInfo.registration_number || '' },
                        { key: 'vatNumber', label: 'VAT Number (optional)', value: businessInfo.vat_tax_number || '' },
                        { key: 'eoriNumber', label: 'EORI / Import-Export Number', value: businessInfo.eori_import_export_number || '' },
                    ],
                    otherShipping: otherShippingAddresses.map((addr) =>
                        mapAddressToFields(addr)
                    ),
                });

                setEditingCards({
                    customerInfo: false,
                    personalAddress: false,
                    businessInfo: false,
                    defaultShipping: false,
                    otherShipping: otherShippingAddresses.map(() => false),
                });
            } catch (err) {
                console.error('Failed to load profile:', err);
                showToast('Failed to load profile.', 'error');
            }
        };

        loadProfile();
    }, [isAdmin, orderId, showToast]);

    const handleFieldChange = (cardKey, fieldKey, value, index = null) => {
        if (readOnly) return;
        setCards((prev) => {
            const updated = { ...prev };
            if (cardKey === 'otherShipping' && index !== null) {
                updated[cardKey][index] = updated[cardKey][index].map((f) =>
                    f.key === fieldKey ? { ...f, value } : f
                );
            } else {
                updated[cardKey] = updated[cardKey].map((f) =>
                    f.key === fieldKey ? { ...f, value } : f
                );
            }
            return updated;
        });
        setHasChanges(true);
    };

    const toggleEdit = (cardKey, index = null) => {
        if (readOnly) return;
        if (cardKey === 'otherShipping' && index !== null) {
            setEditingCards((prev) => {
                const updated = [...prev.otherShipping];
                updated[index] = !updated[index];
                return { ...prev, otherShipping: updated };
            });
        } else {
            setEditingCards((prev) => ({
                ...prev,
                [cardKey]: !prev[cardKey],
            }));
        }
    };

    const handleAddOtherShipping = () => {
        if (cards.otherShipping.length >= 3) {
            showToast('You can only add up to 3 shipping addresses.', 'warning');
            return;
        }

        const newAddressFields = [
            { key: 'street', label: 'Street Address', value: '' },
            { key: 'suburb', label: 'Suburb / Locality', value: '' },
            { key: 'city', label: 'City / Town', value: '' },
            { key: 'state', label: 'State / Province / Region', value: '' },
            { key: 'postal', label: 'Postal / ZIP Code', value: '' },
            { key: 'country', label: 'Country', value: '' },
        ];

        setCards((prev) => ({
            ...prev,
            otherShipping: [...prev.otherShipping, newAddressFields],
        }));

        setEditingCards((prev) => ({
            ...prev,
            otherShipping: [...prev.otherShipping, true],
        }));

        setExpanded('otherShipping');
        setHasChanges(true);
    };

    const handleSave = async () => {
        if (readOnly) return;
        try {
            const fullName =
                cards.customerInfo.find((f) => f.key === 'fullName')?.value || '';
            const email =
                cards.customerInfo.find((f) => f.key === 'email')?.value || '';
            const mobile =
                cards.customerInfo.find((f) => f.key === 'mobile')?.value || '';

            if (subUserMode) {
                await updateProfile({
                    full_name: fullName,
                    mobile_no: mobile,
                });
                showToast('Profile saved successfully!', 'success');
                setHasChanges(false);
                const profile = await getProfile();
                setProfileData(profile);
                if (profile?.account_type) {
                    setAccountType(profile.account_type);
                }
                setCards((prev) => ({
                    ...prev,
                    customerInfo: [
                        { key: 'fullName', label: 'Full Name', value: profile.full_name || '' },
                        { key: 'email', label: 'Email Address', value: profile.email || '' },
                        { key: 'mobile', label: 'Mobile No.', value: profile.mobile_no || '' },
                    ],
                }));
                setEditingCards((prev) => ({ ...prev, customerInfo: false }));
                return;
            }

            // Helper to check if address has at least one non-empty field
            const hasAddressData = (fields) => {
                return fields.some(f => f.value && f.value.trim() !== '');
            };

            // Helper to check if address has required fields filled
            const hasRequiredFields = (fields) => {
                const requiredKeys = ['street', 'city', 'state', 'postal'];
                return requiredKeys.every(key => {
                    const field = fields.find(f => f.key === key);
                    return field && field.value && field.value.trim() !== '';
                });
            };

            const buildAddress = (
                fields,
                isDefault = false,
                isBilling = false,
                isShipping = false,
                label = 'Address',
                id = null
            ) => {
                // Build address object - always include all fields, even if empty
                // This ensures addresses are properly saved and not lost
                const addressObj = {
                    id,
                    ...fields.reduce(
                        (acc, f) => {
                            const mappedKey = fieldMap[f.key] || f.key;
                            // Always include the field value, even if empty string
                            // Empty strings are better than null for address updates
                            const value = (f.value !== undefined && f.value !== null) ? String(f.value) : '';
                            acc[mappedKey] = value;
                            return acc;
                        },
                        {}
                    ),
                    is_default: isDefault,
                    is_billing: isBilling,
                    is_shipping: isShipping,
                    label,
                    recipient_name: fullName || '',
                    phone_number: mobile || '',
                };
                return addressObj;
            };

            const otherShippingWithIds = cards.otherShipping
                .map((fields, idx) => {
                    const existingId =
                        profileData?.addresses?.filter(
                            (a) => a.is_shipping && !a.is_default
                        )?.[idx]?.id || null;
                    return { fields, existingId, idx };
                })
                .filter(({ fields }) => hasAddressData(fields))
                .map(({ fields, existingId }) => 
                    buildAddress(fields, false, false, true, 'Other Shipping', existingId)
                );

            const addresses = [
                // Always include billing address if it exists (even if some fields are empty)
                // This prevents billing address from being deleted when user edits
                ...(cards.personalAddress.length > 0
                    ? [
                        buildAddress(
                            cards.personalAddress,
                            false,
                            true,
                            false,
                            'Personal Address',
                            profileData?.addresses?.find((a) => a.is_billing)?.id
                        ),
                    ]
                    : []),
                // Always include default shipping address if it exists
                ...(cards.defaultShipping.length > 0
                    ? [
                        buildAddress(
                            cards.defaultShipping,
                            true,
                            false,
                            true,
                            'Default Shipping',
                            profileData?.addresses?.find(
                                (a) => a.is_shipping && a.is_default
                            )?.id
                        ),
                    ]
                    : []),
                // Include other shipping addresses (already filtered for data)
                ...otherShippingWithIds,
            ];

            const payload = {
                full_name: fullName,
                email,
                mobile_no: mobile,
                account_type: accountType,
                businessInformation: [
                    {
                        legal_business_name:
                            cards.businessInfo.find((f) => f.key === 'legalName')
                                ?.value || '',
                        registration_number:
                            cards.businessInfo.find((f) => f.key === 'regNumber')
                                ?.value || '',
                        vat_tax_number: (() => {
                            const raw = cards.businessInfo.find((f) => f.key === 'vatNumber')?.value;
                            const s = typeof raw === 'string' ? raw.trim() : '';
                            return s || null;
                        })(),
                        eori_import_export_number:
                            cards.businessInfo.find((f) => f.key === 'eoriNumber')
                                ?.value || '',
                    },
                ],
                addresses,
            };

            await updateProfile(payload);
            showToast('Profile saved successfully!', 'success');
            setHasChanges(false);
            
            // Reload profile data to reflect changes
            try {
                let profile;
                if (isAdmin && orderId) {
                    profile = await getProfileByOrderId(orderId);
                } else {
                    profile = await getProfile();
                }

                setProfileData(profile);

                const personalAddress =
                    profile.addresses?.find((addr) => addr.is_billing) || {};

                const defaultShipping =
                    profile.addresses?.find(
                        (addr) => addr.is_shipping && addr.is_default
                    ) || {};

                const otherShippingAddresses =
                    profile.addresses?.filter(
                        (addr) => addr.is_shipping && !addr.is_default
                    ) || [];

                const businessInfo = profile.businessInformation?.[0] || {};

                if (profile.account_type) {
                    setAccountType(profile.account_type);
                }

                const mapAddressToFields = (addr) => {
                    // Always return all fields, even if address is empty object
                    // This ensures the UI always shows all address fields
                    if (!addr || typeof addr !== 'object') {
                        addr = {};
                    }
                    return [
                        { key: 'street', label: 'Street Address', value: addr.street_address ?? '' },
                        { key: 'suburb', label: 'Suburb / Locality', value: addr.suburb_locality ?? '' },
                        { key: 'city', label: 'City / Town', value: addr.city_town ?? '' },
                        { key: 'state', label: 'State / Province / Region', value: addr.state_province_region ?? '' },
                        { key: 'postal', label: 'Postal / ZIP Code', value: addr.postal_zip_code ?? '' },
                        { key: 'country', label: 'Country', value: addr.country ?? '' },
                    ];
                };

                setCards({
                    customerInfo: [
                        { key: 'fullName', label: 'Full Name', value: profile.full_name || '' },
                        { key: 'email', label: 'Email Address', value: profile.email || '' },
                        { key: 'mobile', label: 'Mobile No.', value: profile.mobile_no || '' },
                    ],
                    personalAddress: mapAddressToFields(personalAddress),
                    defaultShipping: mapAddressToFields(defaultShipping),
                    businessInfo: [
                        { key: 'legalName', label: 'Legal Business Name', value: businessInfo.legal_business_name || '' },
                        { key: 'regNumber', label: 'Registration Number (optional)', value: businessInfo.registration_number || '' },
                        { key: 'vatNumber', label: 'VAT Number (optional)', value: businessInfo.vat_tax_number || '' },
                        { key: 'eoriNumber', label: 'EORI / Import-Export Number', value: businessInfo.eori_import_export_number || '' },
                    ],
                    otherShipping: otherShippingAddresses.map((addr) =>
                        mapAddressToFields(addr)
                    ),
                });

                setEditingCards({
                    customerInfo: false,
                    personalAddress: false,
                    businessInfo: false,
                    defaultShipping: false,
                    otherShipping: otherShippingAddresses.map(() => false),
                });
            } catch (reloadErr) {
                console.error('Failed to reload profile after save:', reloadErr);
                // Don't show error toast as save was successful
            }
        } catch (err) {
            console.error('Failed to save profile:', err);
            showToast('Failed to save profile. Please try again.', 'error');
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid item xs={12} md={6} sx={{ flex: 1 }}>
                    {['customerInfo', 'personalAddress', 'businessInfo'].map(
                        (key) => {
                            const isBusinessDisabled =
                                accountType === 'Individual' &&
                                key === 'businessInfo';

                            const cardReadOnly =
                                readOnly || (subUserMode && key !== 'customerInfo');

                            return (
                                <Accordion
                                    key={key}
                                    expanded={!isBusinessDisabled && expanded === key}
                                    onChange={
                                        !isBusinessDisabled
                                            ? handleAccordionChange(key)
                                            : undefined
                                    }
                                    sx={{
                                        ...accordionSx,
                                        opacity: isBusinessDisabled ? 0.5 : 1,
                                        pointerEvents: isBusinessDisabled
                                            ? 'none'
                                            : 'auto',
                                    }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            color="text.primary"
                                        >
                                            {key === 'customerInfo'
                                                ? 'Customer Information'
                                                : key === 'personalAddress'
                                                ? 'Billing Address'
                                                : 'Business Information'}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={accordionDetailsSx}>
                                        <InfoCard
                                            title=""
                                            fields={cards[key]}
                                            isEditing={editingCards[key]}
                                            toggleEdit={() => toggleEdit(key)}
                                            onFieldChange={(field, value) =>
                                                handleFieldChange(key, field, value)
                                            }
                                            showEdit={!cardReadOnly}
                                            readOnly={cardReadOnly}
                                            lockedFieldKeys={
                                                subUserMode && key === 'customerInfo'
                                                    ? ['email']
                                                    : []
                                            }
                                        />

                                        {key === 'customerInfo' && !subUserMode && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="flex-start"
                                            mt={2}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ mr: 2, fontWeight: 500 }}
                                            >
                                                Account Type:
                                            </Typography>
                                            <FormControl component="fieldset">
                                                <RadioGroup
                                                    row
                                                    value={accountType}
                                                    onChange={(e) => {
                                                        if (!editingCards.customerInfo) return;
                                                        setAccountType(e.target.value);
                                                        setHasChanges(true);
                                                    }}
                                                >
                                                    <StyledFormControlLabel
                                                        value="Individual"
                                                        control={
                                                            <StyledRadio
                                                                size="small"
                                                                sx={{
                                                                    pointerEvents: editingCards.customerInfo ? 'auto' : 'none',
                                                                }}
                                                            />
                                                        }
                                                        label="Individual"
                                                    />
                                                    <StyledFormControlLabel
                                                        value="Business"
                                                        control={
                                                            <StyledRadio
                                                                size="small"
                                                                sx={{
                                                                    pointerEvents: editingCards.customerInfo ? 'auto' : 'none',
                                                                }}
                                                            />
                                                        }
                                                        label="Business"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Box>
                                    )}
                                    </AccordionDetails>
                                </Accordion>
                            );
                        }
                    )}
                </Grid>

                <Grid item xs={12} md={6} sx={{ flex: 1 }}>
                    <Accordion
                        expanded={expanded === 'defaultShipping'}
                        onChange={handleAccordionChange('defaultShipping')}
                        sx={accordionSx}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                                Shipping Address - Default
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={accordionDetailsSx}>
                            <InfoCard
                                title=""
                                fields={cards.defaultShipping}
                                isEditing={editingCards.defaultShipping}
                                toggleEdit={() => toggleEdit('defaultShipping')}
                                onFieldChange={(field, value) =>
                                    handleFieldChange('defaultShipping', field, value)
                                }
                                showEdit={!readOnly && !subUserMode}
                                readOnly={readOnly || subUserMode}
                            />
                        </AccordionDetails>
                    </Accordion>

                    <Accordion
                        expanded={expanded === 'otherShipping'}
                        onChange={handleAccordionChange('otherShipping')}
                        sx={accordionSx}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                                Other Shipping Addresses
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={accordionDetailsSx}>
                            {cards.otherShipping.map((fields, index) => {
                                // Find the actual address object for this index
                                const otherShippingAddresses = profileData?.addresses?.filter(
                                    (addr) => addr.is_shipping && !addr.is_default
                                ) || [];
                                const address = otherShippingAddresses[index];
                                
                                return (
                                    <Box key={index} mb={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                            >
                                                Shipping Address {index + 1}
                                            </Typography>
                                            {!readOnly && !subUserMode && address && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={async () => {
                                                        try {
                                                            await updateAddress(address.id, { is_default: true });
                                                            showToast('Address set as default successfully', 'success');
                                                            // Reload profile data to refresh
                                                            const profile = await getProfile();
                                                            setProfileData(profile);
                                                            
                                                            // Re-map addresses to update cards
                                                            const p = profile?.profile || profile;
                                                            const addresses = Array.isArray(p?.addresses) ? p.addresses : [];
                                                            const personalAddress = addresses.find((addr) => addr.is_billing) || {};
                                                            const defaultShipping = addresses.find((addr) => addr.is_shipping && addr.is_default) || {};
                                                            const otherShippingAddresses = addresses.filter((addr) => addr.is_shipping && !addr.is_default) || [];
                                                            
                                                            const mapAddressToFields = (addr) => [
                                                                { key: 'street', label: 'Street Address', value: addr.street_address || '' },
                                                                { key: 'suburb', label: 'Suburb / Locality', value: addr.suburb_locality || '' },
                                                                { key: 'city', label: 'City / Town', value: addr.city_town || '' },
                                                                { key: 'state', label: 'State / Province / Region', value: addr.state_province_region || '' },
                                                                { key: 'postal', label: 'Postal / ZIP Code', value: addr.postal_zip_code || '' },
                                                                { key: 'country', label: 'Country', value: addr.country || '' },
                                                            ];
                                                            
                                                            setCards(prev => ({
                                                                ...prev,
                                                                defaultShipping: mapAddressToFields(defaultShipping),
                                                                otherShipping: otherShippingAddresses.map(mapAddressToFields),
                                                            }));
                                                        } catch (error) {
                                                            showToast('Failed to set as default', 'error');
                                                        }
                                                    }}
                                                    sx={{ fontSize: '0.75rem', minWidth: '120px' }}
                                                >
                                                    Set as Default
                                                </Button>
                                            )}
                                        </Box>

                                        <InfoCard
                                            title=""
                                            fields={fields}
                                            isEditing={editingCards.otherShipping[index]}
                                            toggleEdit={() =>
                                                toggleEdit('otherShipping', index)
                                            }
                                            onFieldChange={(field, value) =>
                                                handleFieldChange(
                                                    'otherShipping',
                                                    field,
                                                    value,
                                                    index
                                                )
                                            }
                                            onDelete={() => {
                                                setCards((prev) => ({
                                                    ...prev,
                                                    otherShipping:
                                                        prev.otherShipping.filter(
                                                            (_, i) => i !== index
                                                        ),
                                                }));
                                                setEditingCards((prev) => ({
                                                    ...prev,
                                                    otherShipping:
                                                        prev.otherShipping.filter(
                                                            (_, i) => i !== index
                                                        ),
                                                }));
                                                setHasChanges(true);
                                            }}
                                            showEdit={!readOnly && !subUserMode}
                                            readOnly={readOnly || subUserMode}
                                        />
                                    </Box>
                                );
                            })}

                            {!readOnly && !subUserMode && cards.otherShipping.length < 3 && (
                                <Box display="flex" justifyContent="flex-start" mt={1}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddOtherShipping}
                                        size="small"
                                    >
                                        Add Shipping Address
                                    </Button>
                                </Box>
                            )}

                            {!readOnly && !subUserMode && cards.otherShipping.length >= 3 && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 1 }}
                                >
                                    Maximum of 3 shipping addresses allowed.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>

            {!readOnly && hasChanges && (
                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={handleSave}
                        sx={{
                            px: 2.5,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 1.5,
                            boxShadow: 'none',
                            '&:hover': { boxShadow: 2 },
                        }}
                    >
                        Save
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default UserProfile;